import { pb, uploadWithProgress } from "@/lib/pocketbase";
import { offlineStore } from "./offline/offline-store";
import type { ListOptions, Repository } from "./repository";

/**
 * Schreibzugriffe auf diese Collections werden bei fehlendem Netz nicht mit
 * einem Fehler quittiert, sondern in eine Warteschlange gelegt und beim
 * nächsten Sync (Kreis-Pfeile-Symbol, siehe offline-sync.service.ts) der
 * Reihe nach nachgeholt – unabhängig davon, ob sich der Datensatz online
 * inzwischen geändert hat (anders als bei Notizen/Arbeits-PDF gibt es hier
 * keinen Zeilen-/Seiten-Abgleich, nur "zuletzt gewinnt").
 *
 * `note_documents` und `materials` sind bewusst NICHT dabei – die haben ihren
 * eigenen, feineren Offline-Weg (Basis-Stand + Konflikt-Erkennung, siehe
 * offline-sync.service.ts) und würden sich mit einer zweiten, gröberen
 * Warteschlange hier nur ins Gehege kommen.
 */
const OFFLINE_QUEUEABLE = new Set(["homework", "topics", "subjects"]);

export interface QueuedWrite {
  collection: string;
  op: "create" | "update" | "remove";
  id?: string;
  data?: Record<string, unknown>;
  queuedAt: string;
}

function queueWrite(entry: QueuedWrite): Promise<void> {
  // Zeitstempel-Präfix macht die Schlüssel chronologisch sortierbar (siehe
  // offline-sync.service.ts: Reihenfolge zählt für lokale Verweise).
  return offlineStore.set(`opqueue:${Date.now()}_${crypto.randomUUID()}`, entry);
}

/**
 * Erzeugt ein an eine PocketBase-Collection gebundenes Repository.
 * Kapselt alle rohen SDK-Aufrufe an einer Stelle.
 *
 * Lesezugriffe laufen über einen kurzlebigen Cache pro Collection. Ohne ihn
 * holt jeder Seitenwechsel dieselben Fächer/Themen/Stundenplandaten erneut vom
 * Server – das ist die spürbare Wartezeit beim Navigieren. Geschrieben wird
 * nie aus dem Cache: jede Änderung wirft den Cache der Collection weg, der
 * nächste Lesezugriff holt frisch. Gespeichert wird das *Promise*, damit
 * gleichzeitige Abfragen derselben Liste zu einem Request verschmelzen.
 */
const TTL_MS = 60_000;

// Das globale 10s-Limit in lib/pocketbase.ts ist für Datei-Uploads zu knapp
// (eine 8MB-Datei kann auf normalem WLAN länger brauchen) – Server sah dann
// mitten im Empfang der Datei die Verbindung abreißen ("unexpected EOF").
const UPLOAD_TIMEOUT_MS = 120_000;

interface Entry {
  at: number;
  value: Promise<unknown>;
}

const caches = new Map<string, Map<string, Entry>>();

function cacheOf(collection: string): Map<string, Entry> {
  let c = caches.get(collection);
  if (!c) caches.set(collection, (c = new Map()));
  return c;
}

/** Cache einer Collection verwerfen (nach jedem Schreibzugriff). */
export function invalidateCollection(collection: string): void {
  caches.get(collection)?.clear();
}

/** Kompletten Cache verwerfen (Benutzerwechsel, Abmelden). */
export function invalidateAll(): void {
  caches.clear();
}

// Nach An-/Abmelden gehören die Daten einem anderen Konto.
pb.authStore.onChange(() => invalidateAll());

function cached<T>(collection: string, key: string, load: () => Promise<T>): Promise<T> {
  const cache = cacheOf(collection);
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.value as Promise<T>;
  const value = load();
  cache.set(key, { at: Date.now(), value });
  // Fehler nicht festhalten – sonst schlägt auch der nächste Versuch fehl.
  void value.catch(() => {
    if (cache.get(key)?.value === value) cache.delete(key);
  });
  return value;
}

export function pbRepository<T>(collection: string): Repository<T> {
  const col = () => pb.collection(collection);

  return {
    list(opts: ListOptions = {}): Promise<T[]> {
      const { filter, sort, expand, page = 1, perPage = 200 } = opts;
      const key = JSON.stringify([filter ?? "", sort ?? "", expand ?? "", page, perPage]);
      return cached(collection, key, async () => {
        const res = await col().getList(page, perPage, {
          ...(filter ? { filter } : {}),
          ...(sort ? { sort } : {}),
          ...(expand ? { expand } : {}),
        });
        return res.items as unknown as T[];
      });
    },

    getOne(id: string, opts: { expand?: string } = {}): Promise<T> {
      return cached(collection, `one:${id}:${opts.expand ?? ""}`, async () => {
        return (await col().getOne(
          id,
          opts.expand ? { expand: opts.expand } : {}
        )) as unknown as T;
      });
    },

    async create(data: Partial<T> | FormData, onProgress?: (fraction: number) => void): Promise<T> {
      try {
        let out: T;
        if (data instanceof FormData && onProgress) {
          out = await uploadWithProgress<T>(
            `/api/collections/${collection}/records`,
            "POST",
            data,
            onProgress,
            UPLOAD_TIMEOUT_MS
          );
        } else {
          const opts = data instanceof FormData ? { signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS) } : undefined;
          out = (await col().create(data as Record<string, unknown>, opts)) as unknown as T;
        }
        invalidateCollection(collection);
        return out;
      } catch (err) {
        if (!OFFLINE_QUEUEABLE.has(collection) || navigator.onLine || data instanceof FormData) throw err;
        const localId = `local_${crypto.randomUUID()}`;
        const now = new Date().toISOString();
        await queueWrite({ collection, op: "create", id: localId, data: { ...data, id: localId }, queuedAt: now });
        // Erscheint erst nach dem Sync in Listen vom Server – wer sofort damit
        // weiterarbeiten will (Navigation, Verknüpfung), sieht bis dahin diesen
        // lokalen Platzhalter-Datensatz.
        return { ...data, id: localId, created: now, updated: now } as unknown as T;
      }
    },

    async update(id: string, data: Partial<T> | FormData, onProgress?: (fraction: number) => void): Promise<T> {
      try {
        let out: T;
        if (data instanceof FormData && onProgress) {
          out = await uploadWithProgress<T>(
            `/api/collections/${collection}/records/${id}`,
            "PATCH",
            data,
            onProgress,
            UPLOAD_TIMEOUT_MS
          );
        } else {
          const opts = data instanceof FormData ? { signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS) } : undefined;
          out = (await col().update(id, data as Record<string, unknown>, opts)) as unknown as T;
        }
        invalidateCollection(collection);
        return out;
      } catch (err) {
        if (!OFFLINE_QUEUEABLE.has(collection) || navigator.onLine || data instanceof FormData) throw err;
        await queueWrite({ collection, op: "update", id, data: data as Record<string, unknown>, queuedAt: new Date().toISOString() });
        return { ...data, id } as unknown as T;
      }
    },

    async remove(id: string): Promise<void> {
      try {
        await col().delete(id);
        invalidateCollection(collection);
      } catch (err) {
        if (!OFFLINE_QUEUEABLE.has(collection) || navigator.onLine) throw err;
        await queueWrite({ collection, op: "remove", id, queuedAt: new Date().toISOString() });
      }
    },
  };
}
